/**
 * PerspChart - shared opinion-shift line chart component.
 *
 * Y-axis = 4-point opinion scale (Strongly disagree → Strongly agree).
 * Lines represent the mean position per perspective on that scale across phases.
 * Endpoint labels show perspective name only; no numbers on the chart.
 *
 * Props:
 *   series      - [{ pid, shortName, data: [mean_before, mean_during, mean_after] }]
 *                 where mean is a float on the 1–4 scale
 *   categories  - ['Before', 'During', 'After'] (translated)
 *   scaleLabels - [strongly_agree, agree, disagree, strongly_disagree] (top→bottom, translated)
 *   phase       - 'before' | 'during' | 'after'
 *   isTablet    - boolean
 */
import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { PERSPECTIVES } from './perspectives.js';

export const PERSP_IDS = ['participants', 'experts', 'future_leaders', 'public'];

/**
 * Split a label into 1 or 2 lines at the word boundary closest to the midpoint.
 * Labels ≤11 chars stay on one line; longer ones are balanced across two.
 */
function splitLabel(label) {
  if (label.length <= 11) return [label];
  const words = label.split(' ');
  if (words.length < 2) return [label];
  let bestIdx = 1, bestDiff = Infinity;
  for (let i = 1; i < words.length; i++) {
    const diff = Math.abs(
      words.slice(0, i).join(' ').length - words.slice(i).join(' ').length
    );
    if (diff < bestDiff) { bestDiff = diff; bestIdx = i; }
  }
  return [words.slice(0, bestIdx).join(' '), words.slice(bestIdx).join(' ')];
}

export const PERSP_COLOR = {
  participants:   '#86CBF1',
  experts:        '#7ECBA5',
  future_leaders: '#D4A244',
  public:         '#E88B7A',
};

export const PERSP_SVG_DASH = {
  participants:   undefined,
  experts:        '8 6',
  future_leaders: '3 4',
  public:         '10 4 2 4',
};

export const PHASE_ORDER = ['before', 'during', 'after'];

export function getPerspName(pid, lang) {
  const p = PERSPECTIVES.find((pp) => pp.id === pid);
  if (!p) return pid;
  if (lang === 'de') return p.name_de;
  if (lang === 'fr') return p.name_fr;
  return p.name_en;
}

export function getPerspShortName(pid, lang) {
  if (pid === 'participants') {
    if (lang === 'de') return 'Teilnehmer';
    if (lang === 'fr') return 'Participants';
    return 'Participants';
  }
  return getPerspName(pid, lang);
}

// Internal: compute mean position on 1–4 scale from a distribution object.
function computeMean(dist) {
  return (
    dist.strongly_disagree * 1 +
    dist.disagree          * 2 +
    dist.agree             * 3 +
    dist.strongly_agree    * 4
  ) / 100;
}

/**
 * Build chart series from thesis data.
 * Each series data point is the mean position on the 1–4 opinion scale.
 */
export function buildSeries(thesis, lang) {
  return PERSP_IDS.map((pid) => {
    const r = thesis.results[pid];
    return {
      pid,
      name:      getPerspName(pid, lang),
      shortName: getPerspShortName(pid, lang),
      data: [
        computeMean(r.before.dist),
        computeMean(r.during.dist),
        computeMean(r.after.dist),
      ],
    };
  });
}

/**
 * Y bounds are always fixed at the full 1–4 opinion scale.
 * Signature kept for backward compatibility.
 */
export function computeYBounds() {
  return { yMin: 1, yMax: 4 };
}

/**
 * Compute insight card data from distribution-level thesis data.
 * Returns:
 *   hasShift   - bool (false at 'before' phase - only one data point)
 *   largest    - perspective with highest rise in strongly_agree
 *   stable     - perspective with smallest absolute change in total Zustimmung
 *   gapHigh    - perspective with highest Zustimmung at current phase
 *   gapLow     - perspective with lowest  Zustimmung at current phase
 */
export function computeInsightCards(thesis, phase) {
  const phaseIdx = PHASE_ORDER.indexOf(phase);
  const hasShift = phaseIdx > 0;

  const persp = PERSP_IDS.map((pid) => {
    const r = thesis.results[pid];
    return {
      pid,
      beforeZ:  r.before.agree,
      currentZ: r[phase].agree,
      deltaZ:   r[phase].agree - r.before.agree,
    };
  });

  // Largest: perspective with the greatest absolute change in top-two-box agreement
  const byAbsDeltaZ = [...persp].sort((a, b) => Math.abs(b.deltaZ) - Math.abs(a.deltaZ));
  const largest     = byAbsDeltaZ[0];

  // Stable: smallest absolute change, must differ from largest
  const stable = [...persp]
    .filter((p) => p.pid !== largest.pid)
    .sort((a, b) => Math.abs(a.deltaZ) - Math.abs(b.deltaZ))[0];

  const byCurrentZ = [...persp].sort((a, b) => b.currentZ - a.currentZ);
  const gapHigh    = byCurrentZ[0];
  const gapLow     = byCurrentZ[byCurrentZ.length - 1];

  return { hasShift, largest, stable, gapHigh, gapLow };
}

// ── Component ─────────────────────────────────────────────────────────────────

// The 4 integer levels, top → bottom. Index maps to scaleLabels[].
const SCALE_LEVELS = [4, 3, 2, 1];

export default function PerspChart({ series, categories, scaleLabels, phase, isTablet }) {
  const wrapRef           = useRef(null);
  const [wrapW, setWrapW] = useState(320);
  const prevPhaseRef      = useRef(phase);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setWrapW(el.offsetWidth);
    update();
    const obs = new ResizeObserver(update);
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // ── Entrance animation ─────────────────────────────────────────────────────
  // isEntered starts false so the initial render hides everything.
  // entryAnimPending is set true just before setIsEntered triggers a re-render;
  // reading it in that render lets us apply the transition independently of
  // isAdvancing (which is always false on mount since no phase change happened).
  const [isEntered, setIsEntered]   = useState(false);
  const entryAnimPending            = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => {
      entryAnimPending.current = true;
      setIsEntered(true);
    }, 350);
    return () => clearTimeout(t);
  }, []);

  // Consume the pending flag on the render where isEntered just flipped.
  const isEnteringNow = entryAnimPending.current;
  if (isEnteringNow) entryAnimPending.current = false;

  const prevPhaseIdx = PHASE_ORDER.indexOf(prevPhaseRef.current);
  const currPhaseIdx = PHASE_ORDER.indexOf(phase);
  const isAdvancing  = currPhaseIdx > prevPhaseIdx;

  useEffect(() => {
    prevPhaseRef.current = phase;
  }, [phase]);

  // ── Bubble label width measurement ─────────────────────────────────────────
  // SVGTextElement refs keyed by pid - populated by the label render below.
  // getComputedTextLength() returns the advance width independently of position
  // so we only need to re-measure when shortNames change (i.e. when series or
  // language changes), not on every phase change.
  const textEls     = useRef({});
  const [bubbleWidths, setBubbleWidths] = useState({});

  useLayoutEffect(() => {
    const w = {};
    series.forEach((s) => {
      const el = textEls.current[s.pid];
      if (el && typeof el.getComputedTextLength === 'function') {
        w[s.pid] = el.getComputedTextLength();
      }
    });
    if (Object.keys(w).length) setBubbleWidths(w);
  }, [series]); // re-run when thesis/language changes (shortNames update)

  // ── Geometry ──────────────────────────────────────────────────────────────
  // PR: right breathing margin (and symmetrically the left margin for dots/lines).
  // PL = PR so lines start at the same inset from the left as from the right -
  // this makes them begin right at the label area, spanning the full visual width.
  // GRID_LEFT: where gridlines and y-axis labels START (after the label text).
  const PR        = isTablet ? 37  : 29;
  const PL        = PR;   // symmetric: xAt(0) = PR + dotInset (left edge of label zone)
  const GRID_LEFT = isTablet ? 100 : 88;  // gridlines start here, after label text
  const PT        = isTablet ? 22 : 16;
  const PBL       = 8;

  const svgH  = isTablet
    ? Math.min(700, Math.max(500, Math.round(window.innerHeight * 0.32)))
    : 180;
  const plotW = Math.max(80, wrapW - PL - PR);
  const plotH = svgH - PT - PBL;

  // ── Full 4-point opinion scale, always ───────────────────────────────────
  const Y_MIN = 1;
  const Y_MAX = 4;

  // All four reference lines are always shown.
  const visibleLevels = SCALE_LEVELS;

  // dotInset pushes dot centers inward from the PL/PR gridline boundaries.
  // dotR=7/5 brings outer edges to the boundary; +4 puts them clearly inside.
  // xAt(1) = PL + plotW/2 - midpoint unchanged, so scaleX(0.5) clipPath is correct.
  const dotInset = isTablet ? 11 : 9;
  const xAt = (i) => PL + dotInset + (i / 2) * (plotW - 2 * dotInset);
  const yAt = (v) => PT + (1 - (v - Y_MIN) / (Y_MAX - Y_MIN)) * plotH;

  // dotR must live here - used by the collision avoidance block below.
  const dotR = isTablet ? [7, 7, 7] : [5, 5, 5];

  // ── Bubble label geometry & collision avoidance ───────────────────────────
  // Each endpoint label is a rounded-rect bubble sitting just above its dot.
  // adjBottom = the bubble's BOTTOM y-coordinate (bubble spans adjBottom−bubbleH .. adjBottom).
  // Sort bottommost-first; push upper bubbles further up; top-clamp + propagate down.
  const bPadH     = 11;   // horizontal inner padding (each side)
  const bubbleH   = 22;   // fixed bubble height (fits 13.5px text + ~4px padding top/bottom)
  const bubbleGap = 6;    // px gap between dot top and bubble bottom
  const STEP      = 28;   // min distance between successive adjBottom values (bubbleH + 6 air)
  const chartTop  = PT;

  const lblItems = series
    .map((s) => {
      const dotY = yAt(s.data[currPhaseIdx]);
      const nb   = dotY - dotR[0] - bubbleGap;  // natural bubble bottom
      return { pid: s.pid, dotY, naturalBottom: nb, adjBottom: nb };
    })
    .sort((a, b) => b.naturalBottom - a.naturalBottom);  // bottommost first

  // Bottom-to-top pass: push upper bubbles up when they'd overlap the one below
  for (let i = 1; i < lblItems.length; i++) {
    lblItems[i].adjBottom = Math.min(lblItems[i].adjBottom, lblItems[i - 1].adjBottom - STEP);
  }
  // Top clamp: topmost bubble must not go above the chart top
  const minBottom = chartTop + bubbleH;
  const topIdx    = lblItems.length - 1;
  if (lblItems[topIdx].adjBottom < minBottom) {
    lblItems[topIdx].adjBottom = minBottom;
    // Propagate downward so lower bubbles don't overlap the clamped top ones
    for (let i = topIdx - 1; i >= 0; i--) {
      lblItems[i].adjBottom = Math.max(lblItems[i].adjBottom, lblItems[i + 1].adjBottom + STEP);
    }
  }
  const adjInfo = Object.fromEntries(
    lblItems.map((l) => [l.pid, { adjBottom: l.adjBottom, naturalBottom: l.naturalBottom, dotY: l.dotY }])
  );

  // Shared text style constants.
  const axisFontSz = isTablet ? 11 : 10;
  const nameSz     = 13.5;
  const strokeW    = isTablet ? '7' : '4';

  // Round caps extend strokeW/2 past the geometric endpoint.
  // The mask buffer must be at least that large or the cap gets clipped,
  // making the apparent stroke width narrower at joints than mid-segment.
  const maskBuf = Math.ceil(parseFloat(strokeW) / 2) + 2;

  const seg01 = currPhaseIdx >= 1;
  const seg12 = currPhaseIdx >= 2;

  const EASE_OUT   = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';
  const EASE_INOUT = 'cubic-bezier(0.4, 0, 0.2, 1)'; // matches VotesDetail bar ease

  // maskT / dotT / lblT - separate entrance (slow, deliberate) from phase-advance (quick).
  // CSS width + CSS transform property used throughout for Safari compatibility.
  //
  // IMPORTANT: maskT must NEVER be 'none'. CSS transitions only fire when the
  // transition property was already covering the animated property in the PREVIOUS
  // render. If we set transition:'none' and then switch to transition:'width 0.6s'
  // in the same React commit as a width change, the browser sees the OLD value
  // was 'none' → no transition fires. The fix: always keep 'width <duration>'
  // set on the clip rect, varying only the duration per scenario.
  let maskT, dotT, lblT;
  if (isEnteringNow) {
    // Entrance: slow, deliberate reveal
    maskT = `width 1.4s ${EASE_INOUT}`;
    dotT  = `opacity 1.0s ${EASE_INOUT}`;
    lblT  = `transform 1.2s ${EASE_INOUT}, opacity 0.8s ${EASE_INOUT} 0.25s`;
  } else if (isAdvancing) {
    // Phase advance: smooth and clear
    maskT = `width 0.65s ${EASE_OUT}`;
    dotT  = `opacity 0.45s ${EASE_OUT}`;
    lblT  = `transform 0.65s ${EASE_OUT}, opacity 0.35s ease 0.1s`;
  } else {
    // Backward / already settled - near-instant but transition MUST remain present
    maskT = `width 0.18s ${EASE_OUT}`;
    dotT  = 'opacity 0.15s ease';
    lblT  = 'transform 0.18s ease, opacity 0.15s ease';
  }

  return (
    <div ref={wrapRef} className="td-chart-wrap">
      <svg
        width="100%"
        height={svgH}
        style={{ display: 'block', overflow: 'visible' }}
        aria-hidden="true"
      >
        <defs>
          {/* clipPath - rect grows from left (x fixed) as width increases 0→half→full.
              Width animation is universally supported (incl. Safari); transform/scaleX is not. */}
          {series.map((s) => {
            const fullW  = xAt(2) - xAt(0) + maskBuf * 2;
            const clipW  = (!isEntered || currPhaseIdx === 0) ? 0.5 : fullW * (currPhaseIdx / 2);
            return (
              <clipPath key={`cp-${s.pid}`} id={`cp-${s.pid}`}>
                <rect
                  x={xAt(0) - maskBuf}
                  y={PT - maskBuf}
                  width={clipW}
                  height={plotH + maskBuf * 2}
                  style={{ transition: maskT }}
                />
              </clipPath>
            );
          })}
        </defs>

        {/* ── Y-axis: labels left-aligned at x=PR (symmetric with right margin),    ── */}
        {/* vertically centred on each gridline. Gridlines start at x=GRID_LEFT (after labels). */}
        {scaleLabels && visibleLevels.map((level) => {
          const idx       = SCALE_LEVELS.indexOf(level);
          const ly        = yAt(level);
          const lines     = splitLabel(scaleLabels[idx]);
          const lineH     = Math.round(axisFontSz * 1.28);
          const isTwoLine = lines.length === 2;
          // For 2 lines: line 1 is lineH/2 above the gridline, line 2 is lineH/2 below.
          const halfH = isTwoLine ? lineH / 2 : 0;
          const lx    = PR + 4;   // symmetric with the right breathing margin
          return (
            <g key={level}>
              {/* Gridline starts at GRID_LEFT - after the label text */}
              <line
                x1={GRID_LEFT} y1={ly}
                x2={PL + plotW} y2={ly}
                stroke="rgba(134,203,241,0.07)"
                strokeWidth="1.5"
              />
              {/* Label block - left-aligned, vertically centred on the gridline */}
              <text
                textAnchor="start"
                fill="rgba(134,203,241,0.50)"
                fontSize={axisFontSz}
                fontWeight="500"
                fontFamily="'DM Sans', sans-serif"
              >
                <tspan x={lx} y={ly - halfH} dominantBaseline="middle">{lines[0]}</tspan>
                {isTwoLine && (
                  <tspan x={lx} y={ly + halfH} dominantBaseline="middle">{lines[1]}</tspan>
                )}
              </text>
            </g>
          );
        })}

        {/* ── Lines - single polyline per series, masked left-to-right ── */}
        {series.map((s) => (
          <polyline
            key={s.pid}
            points={`${xAt(0)},${yAt(s.data[0])} ${xAt(1)},${yAt(s.data[1])} ${xAt(2)},${yAt(s.data[2])}`}
            fill="none"
            stroke={PERSP_COLOR[s.pid]}
            strokeWidth={strokeW}
            strokeLinecap="round"
            strokeLinejoin="round"
            clipPath={`url(#cp-${s.pid})`}
          />
        ))}

        {/* ── Dots - plain filled circles, drawn after the clipped polyline ── */}
        {series.map((s) => (
          <g key={s.pid}>
            <circle cx={xAt(0)} cy={yAt(s.data[0])} r={dotR[0]}
              fill={PERSP_COLOR[s.pid]}
              style={{ opacity: isEntered ? 1 : 0, transition: dotT }} />
            <circle cx={xAt(1)} cy={yAt(s.data[1])} r={dotR[1]}
              fill={PERSP_COLOR[s.pid]}
              style={{ opacity: isEntered && seg01 ? 1 : 0, transition: dotT }} />
            <circle cx={xAt(2)} cy={yAt(s.data[2])} r={dotR[2]}
              fill={PERSP_COLOR[s.pid]}
              style={{ opacity: isEntered && seg12 ? 1 : 0, transition: dotT }} />
          </g>
        ))}

        {/* ── Endpoint bubble labels: pill above the active dot, right-edge at dotX ── */}
        {/* Drawn last so the bubble fill renders above chart lines and dots.          */}
        {series.map((s) => {
          const { adjBottom, naturalBottom, dotY } = adjInfo[s.pid];
          const dotX   = xAt(currPhaseIdx);
          // Use measured text width; fall back to char-count estimate until measured.
          const textW  = bubbleWidths[s.pid] || s.shortName.length * 7.5;
          const bubbleW = textW + bPadH * 2;

          return (
            <g key={s.pid}>
              {/* Bubble group - CSS transform property (px units) for Safari-compatible
                  transitions; the SVG transform *attribute* doesn't animate on Safari. */}
              <g
                style={{
                  transform: `translate(${dotX}px, ${adjBottom}px)`,
                  transition: lblT,
                  opacity: isEntered ? 1 : 0,
                }}
              >
                {/* Pill background - right edge at origin (= dotX), top at −bubbleH */}
                <rect
                  x={-bubbleW}
                  y={-bubbleH}
                  width={bubbleW}
                  height={bubbleH}
                  rx="7"
                  ry="7"
                  fill="#102f47"
                  stroke="rgba(255,255,255,0.09)"
                  strokeWidth="1"
                />
                {/* Label text - right-padded inside the pill, vertically centred.
                    dominantBaseline="middle" places the em-square centre at y, which
                    sits above the visual cap-height centre. Instead we use the baseline
                    (dominantBaseline="auto") offset upward by ~cap-height/2 (≈0.35×em). */}
                <text
                  ref={(el) => { if (el) textEls.current[s.pid] = el; }}
                  x={-bPadH}
                  y={-bubbleH / 2 + nameSz * 0.35}
                  textAnchor="end"
                  dominantBaseline="auto"
                  fill={PERSP_COLOR[s.pid]}
                  fontSize={nameSz}
                  fontWeight="600"
                  fontFamily="'DM Sans', sans-serif"
                >
                  {s.shortName}
                </text>
              </g>
            </g>
          );
        })}

        {/* X-axis labels removed - the phase toggle above the chart serves this role */}
      </svg>
    </div>
  );
}
